import { useEffect, useMemo, useState } from "react";
import {
  partnersAccept,
  partnersConfirm,
  partnersInitiate,
  partnersList,
  partnersRevoke,
  partnersUnlink
} from "../api/endpoints";
import { useI18n } from "../i18n";

type LinkRow = {
  id: string;
  code?: string | null;
  status: string;
  direction?: "INCOMING" | "OUTGOING" | null;
  created_at?: string;
  updated_at?: string;
  partner?: {
    user_id?: string | null;
    public_id?: string | null;
    display_name?: string | null;
  } | null;
  partner_group?: {
    group_id?: string | null;
    label?: string | null;
  } | null;
};

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.setAttribute("readonly", "true");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

function explainPartnerError(err: any, fallback: string) {
  const raw =
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  if (raw === "dob_required") {
    return "Date of birth is required before partner linking. Both accounts need DOB / age setup first.";
  }
  if (raw === "invitee_not_found") {
    return "The selected partner account could not be found.";
  }
  if (raw === "cannot_accept_own_invite") {
    return "You cannot accept your own invite.";
  }
  if (raw === "invalid_code") {
    return "That code is invalid or expired.";
  }
  return raw;
}

export default function PartnersPage() {
  
  const urlParams = new URLSearchParams(window.location.search);
  const targetPublicIdFromUrl =
    urlParams.get("invitee_public_id") ||
    urlParams.get("targetPublicId") ||
    "";
  const targetLabelFromUrl =
    urlParams.get("targetLabel") ||
    urlParams.get("targetUserId") ||
    "this user";
  const [urlInviteSending, setUrlInviteSending] = useState(false);
  const [urlInviteMessage, setUrlInviteMessage] = useState<string | null>(null);

  async function sendUrlPartnerInvite() {
    if (!targetPublicIdFromUrl || urlInviteSending) return;

    setUrlInviteSending(true);
    setUrlInviteMessage(null);

    try {
      const res: any = await partnersInitiate(targetPublicIdFromUrl);
      setUrlInviteMessage(
        res?.ok
          ? `Partner request sent to ${targetLabelFromUrl}.`
          : "Partner request created."
      );
    } catch (e: any) {
      const error = e?.body?.error || e?.error || e?.message || "request_failed";
      if (error === "dob_required") {
        setUrlInviteMessage("Date of birth is required before partner linking can continue.");
      } else if (error === "multi_partner_not_enabled") {
        setUrlInviteMessage("Adding another partner requires the multi-partner add-on and consent flow.");
      } else if (error === "age_mismatch") {
        setUrlInviteMessage("This partner link is blocked by age-safety rules.");
      } else {
        setUrlInviteMessage(`Partner request failed: ${error}`);
      }
    } finally {
      setUrlInviteSending(false);
    }
  }

  const { t } = useI18n();
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [code, setCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [inviteePublicId, setInviteePublicId] = useState("");

  const [incomingCode, setIncomingCode] = useState("");
  const [error, setError] = useState<string | null>(null);


  const urlInviteCard = targetPublicIdFromUrl ? (
    <div
      className="card"
      style={{
        marginBottom: 12,
        border: "1px solid color-mix(in srgb, var(--primary) 35%, var(--border))",
      }}
    >
      <div className="section" data-marker="URL_PARTNER_INVITE_CARD">
        <div className="section-title">Ask to become partners</div>
        <p className="text-muted small" style={{ marginTop: 6 }}>
          Send a partner request to {targetLabelFromUrl}. Existing partner and age-safety rules still apply.
        </p>

        {urlInviteMessage && (
          <div className="text-muted small" style={{ marginTop: 8 }}>
            {urlInviteMessage}
          </div>
        )}

        <button
          type="button"
          className="btn-primary"
          onClick={() => void sendUrlPartnerInvite()}
          disabled={urlInviteSending}
          style={{ marginTop: 10 }}
        >
          {urlInviteSending ? "Sending…" : "Send partner request"}
        </button>
      </div>
    </div>
  ) : null;

  const statusLabel = useMemo(() => {
    return (status: string) => {
      const s = String(status || "").toUpperCase();
      if (s === "PENDING_CONFIRMATION") return "Waiting for inviter confirmation";
      if (s === "REVOKED") return "Revoked";
      const key = `partners.status.${s}`;
      const v = t(key);
      return v === key ? status : v;
    };
  }, [t]);

  const shareLink = useMemo(() => {
    if (!code) return "";
    return `${window.location.origin}/partners?code=${encodeURIComponent(code)}`;
  }, [code]);

  async function load() {
    setLoading(true);
    try {
      const res = await partnersList();
      const nextLinks = (res.links as any[]) || [];
      setLinks(nextLinks);

      const outgoing = nextLinks
        .filter((x: any) => {
          const status = String(x?.status || "").toUpperCase();
          return (status === "INVITED" || status === "PENDING_CONFIRMATION") && x?.direction === "OUTGOING";
        })
        .sort((a: any, b: any) => {
          const aa = new Date(a?.updated_at || a?.created_at || 0).getTime();
          const bb = new Date(b?.updated_at || b?.created_at || 0).getTime();
          return bb - aa;
        });

      if (outgoing[0]?.code) setCode(String(outgoing[0].code));
      else setCode(null);

      setError(null);
    } catch (err: any) {
      setError(explainPartnerError(err, t("partners.errors.loadFailed")));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const qCode = url.searchParams.get("code");
    if (qCode && !incomingCode) setIncomingCode(qCode);
  }, [incomingCode]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const qCode = url.searchParams.get("code");
    if (!qCode) return;

    const appUrl = `intimsync://partners?code=${encodeURIComponent(qCode)}`;
    const timer = window.setTimeout(() => {
      // fallback stays on web page
    }, 1200);

    try {
      window.location.href = appUrl;
    } catch {}

    return () => window.clearTimeout(timer);
  }, []);

  async function handleInitiate() {
    if (!inviteePublicId.trim()) {
      alert("Enter the target partner public ID first.");
      return;
    }

    try {
      const res = await partnersInitiate(inviteePublicId.trim());
      const nextCode = res?.code || res?.link?.code || null;
      setCode(nextCode);
      setCopied(false);
      setCopiedLink(false);
      await load();
    } catch (err: any) {
      alert(explainPartnerError(err, t("partners.errors.createInviteFailed")));
    }
  }

  async function handleCopy() {
    if (!code) return;
    const ok = await copyToClipboard(code);
    setCopied(ok);
    if (!ok) alert(t("common.copyFailed"));
  }

  async function handleCopyLink() {
    if (!shareLink) return;
    const ok = await copyToClipboard(shareLink);
    setCopiedLink(ok);
    if (!ok) alert(t("common.copyFailed"));
  }

  async function handleShareLink() {
    if (!shareLink) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: t("partners.title"),
          text: "Open this link and accept the partner invite.",
          url: shareLink,
        });
        return;
      } catch {}
    }

    await handleCopyLink();
  }

  async function handleAccept() {
    if (!incomingCode.trim()) return;
    try {
      await partnersAccept(incomingCode.trim());
      setIncomingCode("");
      setCopied(false);
      setCopiedLink(false);
      await load();
    } catch (err: any) {
      alert(explainPartnerError(err, t("partners.errors.acceptFailed")));
    }
  }

  async function handleConfirm(id: string) {
    try {
      await partnersConfirm(id);
      await load();
    } catch (err: any) {
      alert(explainPartnerError(err, "Could not confirm partnership."));
    }
  }

  async function handleUnlink(id: string) {
    if (!window.confirm(t("partners.confirmUnlink"))) return;
    try {
      await partnersUnlink(id);
      await load();
    } catch (err: any) {
      alert(explainPartnerError(err, t("partners.errors.unlinkFailed")));
    }
  }

  async function handleRevoke(id: string) {
    if (!window.confirm("Revoke this pending invite?")) return;
    try {
      await partnersRevoke(id);
      await load();
    } catch (err: any) {
      alert(explainPartnerError(err, "Could not revoke invite."));
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{t("partners.title")}</div>
          <div className="page-subtitle">{t("partners.subtitle")}</div>
        </div>
      </div>

      {urlInviteCard}

      <div className="card" style={{ marginBottom: "1.2rem" }}>
        <div className="section-title">1. Invite your partner</div>
        <div className="section-description">
          Enter your partner&apos;s public ID and create an invite. Then send them the code or invite link. They accept it, and after that you confirm the partnership.
        </div>

        <div style={{ marginTop: "0.8rem", marginBottom: "0.8rem" }}>
          <input
            className="form-input"
            placeholder="Partner public ID"
            value={inviteePublicId}
            onChange={(e) => setInviteePublicId(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </div>

        <div className="chip-row" style={{ marginTop: "0.6rem" }}>
          <button className="btn-primary" onClick={handleInitiate}>
            Create targeted code
          </button>

          {code && (
            <>
              <button className="btn-outline" onClick={handleCopy}>
                {copied ? t("common.copied") : t("common.copy")}
              </button>
              <button className="btn-outline" onClick={handleCopyLink}>
                {copiedLink ? "Link copied" : "Copy link"}
              </button>
              <button className="btn-outline" onClick={handleShareLink}>
                Share link
              </button>
            </>
          )}
        </div>

        {code && (
          <>
            <p className="text-muted small" style={{ marginTop: "0.6rem" }}>
              Your partner code: <strong>{code}</strong>
            </p>
            <p className="text-muted small" style={{ marginTop: "0.35rem", wordBreak: "break-all" }}>
              Invite link: <strong>{shareLink}</strong>
            </p>
            <p className="text-muted small" style={{ marginTop: "0.35rem" }}>
              If your partner has the app installed, opening the invite link should prefill the code there.
            </p>
          </>
        )}
      </div>

      <div className="card" style={{ marginBottom: "1.2rem" }}>
        <div className="section-title">2. Accept a partner invite</div>
        <div className="section-description">
          Paste the code your partner sent you. After you accept, the inviter still has to confirm the partnership.
        </div>
        <div className="chip-row">
          <input
            className="form-input"
            placeholder="XXXX-XXXX"
            value={incomingCode}
            onChange={(e) => setIncomingCode(e.target.value)}
            style={{ maxWidth: 220 }}
          />
          <button className="btn-outline" onClick={handleAccept}>
            {t("partners.accept")}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="section-title">{t("partners.existingTitle")}</div>

        {loading && <p className="text-muted small">{t("common.loading")}</p>}
        {error && <p className="form-error">{error}</p>}

        {!loading && !links.length && !error && (
          <p className="text-muted small">{t("partners.empty")}</p>
        )}

        {!!links.length && (
          <table className="table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Direction</th>
                <th>Partner</th>
                <th>Code</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {links.map((l) => {
                const isAccepted = String(l.status).toUpperCase() === "ACCEPTED";
                const isInvited = String(l.status).toUpperCase() === "INVITED";
                const isPending = String(l.status).toUpperCase() === "PENDING_CONFIRMATION";
                const isOutgoing = l.direction === "OUTGOING";
                const isIncoming = l.direction === "INCOMING";
                return (
<tr key={l.id}>
                    <td>{statusLabel(l.status)}</td>
                    <td>{l.direction || "—"}</td>
                    <td>{l.partner?.display_name || l.partner?.public_id || l.partner?.user_id || "—"}</td>
                    <td>{l.code || "—"}</td>
                    <td>
                      {l.updated_at
                        ? new Date(l.updated_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "2-digit"
                          })
                        : "—"}
                    </td>
                    <td>
                      {isAccepted && (
                        <div className="chip-row">
                          {l.partner_group?.group_id && (
                            <a
                              className="btn-outline"
                              href={`/groups/${encodeURIComponent(l.partner_group.group_id)}`}
                            >
                              Open shared space
                            </a>
                          )}

                          <button className="btn-danger" onClick={() => handleUnlink(l.id)}>
                            {t("partners.unlink")}
                          </button>
                        </div>
                      )}

                      {isInvited && isOutgoing && (
                        <button className="btn-outline" onClick={() => handleRevoke(l.id)}>
                          Revoke invite
                        </button>
                      )}

                      {isPending && isOutgoing && (
                        <div className="chip-row">
                          <button className="btn-primary" onClick={() => handleConfirm(l.id)}>
                            Confirm partnership
                          </button>
                          <button className="btn-outline" onClick={() => handleRevoke(l.id)}>
                            Cancel invite
                          </button>
                        </div>
                      )}

                      {isPending && isIncoming && (
                        <span className="text-muted small">Waiting for inviter confirmation</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
